import React from 'react';

import { colors } from '../../theme';
import type { CompanyData, FilingData } from '../../types';

interface PaneHeaderProps {
  companyData: CompanyData;
  currentFiling: FilingData;
  selectedFilingIndex: number;
  showFilingDropdown: boolean;
  setShowFilingDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedFilingIndex: React.Dispatch<React.SetStateAction<number>>;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
}

export function PaneHeader({
  companyData,
  currentFiling,
  selectedFilingIndex,
  showFilingDropdown,
  setShowFilingDropdown,
  setSelectedFilingIndex,
  dropdownRef,
  onClose,
}: PaneHeaderProps): React.ReactElement {
  return (
    <div className='shrink-0 px-3 py-2 border-b border-edge'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div className='px-1.5 py-0.5 rounded flex items-center justify-center shrink-0 bg-navy'>
            <span className='text-white text-[10px] font-bold'>
              {companyData.ticker}
            </span>
          </div>
          <div className='text-[13px] font-semibold text-ink'>
            {companyData.name}
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {/* Filing selector dropdown */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowFilingDropdown(prev => !prev)}
              className='flex items-center gap-1 px-2 py-1 rounded transition-colors'
              style={{
                background: showFilingDropdown ? colors.surface : 'transparent',
                border: `1px solid ${colors.edge}`,
                color: colors.inkSecondary,
                fontSize: '10px',
                cursor: 'pointer',
              }}
              onMouseEnter={e => {
                if (!showFilingDropdown) {
                  e.currentTarget.style.background = colors.surface;
                }
              }}
              onMouseLeave={e => {
                if (!showFilingDropdown) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{ fontWeight: 600 }}>
                {currentFiling.filingType}
              </span>
              <span style={{ color: colors.inkMuted }}>·</span>
              <span>{currentFiling.periodEnd}</span>
              <svg
                className='w-3 h-3'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                viewBox='0 0 24 24'
                style={{
                  transform: showFilingDropdown
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.15s ease',
                }}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>
            {/* Dropdown menu */}
            {showFilingDropdown && (
              <div className='absolute top-full right-0 mt-1 bg-white border border-edge rounded-md shadow-lg z-50 min-w-40 overflow-hidden'>
                {companyData.filings.map((filing, idx) => (
                  <button
                    key={filing.id}
                    onClick={() => {
                      setSelectedFilingIndex(idx);
                      setShowFilingDropdown(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 transition-colors border-none text-left cursor-pointer text-[11px] ${idx === selectedFilingIndex ? 'bg-surface' : 'bg-transparent'}`}
                    onMouseEnter={e => {
                      if (idx !== selectedFilingIndex) {
                        e.currentTarget.style.background = colors.surfaceAlt;
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background =
                        idx === selectedFilingIndex
                          ? colors.surface
                          : 'transparent';
                    }}
                  >
                    <div>
                      <span className='font-semibold text-ink'>
                        {filing.filingType}
                      </span>
                      <span className='text-ink-muted ml-1.5'>
                        {filing.periodEnd}
                      </span>
                    </div>
                    {idx === selectedFilingIndex && (
                      <svg className='w-3 h-3 fill-accent' viewBox='0 0 24 24'>
                        <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            className='flex items-center justify-center w-6 h-6 rounded transition-colors bg-transparent border-none text-ink-muted cursor-pointer'
            onMouseEnter={e => {
              e.currentTarget.style.background = colors.surface;
              e.currentTarget.style.color = colors.ink;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = colors.inkMuted;
            }}
            title='Close panel'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              strokeWidth={2}
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
